"use client";

import {
  ArrowLeftDoubleSVG,
  ArrowLeftSVG,
  ArrowRightDoubleSVG,
  ArrowRightSVG,
} from "@/utils/svg";
import { cn } from "@/utils/utils";

interface ListPaginationProps {
  readonly currentPage: number;
  readonly setCurrentPage: (page: number) => void;
  readonly totalItems: number;
  readonly itemsPerPage: number;
  readonly itemsLabel?: string;
}

const CONTROL_BUTTON_CLASS =
  "flex size-[32px] shrink-0 items-center justify-center rounded-full border border-[#F1F1F1] bg-white hover:bg-[#F5F5F5] disabled:cursor-default disabled:opacity-50";

const PAGE_BUTTON_INACTIVE_CLASS =
  "flex size-[32px] shrink-0 items-center justify-center rounded-full border border-[#F1F1F1] bg-white text-[14px] font-semibold text-[#1F1F1F] hover:bg-[#F5F5F5]";

const PAGE_BUTTON_ACTIVE_CLASS =
  "flex size-[32px] shrink-0 items-center justify-center rounded-full bg-[#5C88C5] text-[14px] font-semibold text-white hover:bg-[#496fa4]";

const PAGINATION_BAR_CLASS =
  "relative h-[60px] w-full rounded-[8px] bg-white px-[18px] shadow-[0_1px_11.7px_1px_rgba(185,185,185,0.08)]";

const PAGINATION_WRAPPER_CLASS = "w-full shrink-0 py-[24px]";

export default function ListPagination({
  currentPage,
  setCurrentPage,
  totalItems,
  itemsPerPage,
  itemsLabel = "Items",
}: ListPaginationProps) {
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const renderPageNumbers = () => {
    const pageNumbers: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1);

      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      if (currentPage <= 3) {
        startPage = 2;
        endPage = Math.min(totalPages - 1, maxVisiblePages - 2);
      } else if (currentPage >= totalPages - 2) {
        startPage = Math.max(2, totalPages - (maxVisiblePages - 3));
        endPage = totalPages - 1;
      }

      if (startPage > 2) {
        pageNumbers.push("...");
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }

      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((page, index) => {
      const keySuffix = page === "..." ? `ellipsis-${index}` : page;
      const key = `page-${keySuffix}`;

      if (page === "...") {
        return (
          <div
            key={key}
            className="flex size-[32px] shrink-0 items-center justify-center rounded-[8px] bg-white text-[13px] font-semibold text-[#1F1F1F]"
          >
            ...
          </div>
        );
      }

      const isActive = currentPage === page;

      return (
        <button
          key={key}
          type="button"
          className={isActive ? PAGE_BUTTON_ACTIVE_CLASS : PAGE_BUTTON_INACTIVE_CLASS}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </button>
      );
    });
  };

  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  if (totalPages <= 0 && totalItems <= 0) {
    return (
      <div className={PAGINATION_WRAPPER_CLASS}>
        <div className={PAGINATION_BAR_CLASS} />
      </div>
    );
  }

  return (
    <div className={PAGINATION_WRAPPER_CLASS}>
      <div className={PAGINATION_BAR_CLASS}>
        <div className="relative flex h-full items-center justify-between">
        <div className="z-[1] shrink-0 text-[14px] font-normal leading-normal text-[#1F1F1F]">
          {totalItems > 0
            ? `${startItem}-${endItem} of ${totalItems} ${itemsLabel}`
            : `No ${itemsLabel}`}
        </div>

        <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-[5px]">
          <button
            type="button"
            className={CONTROL_BUTTON_CLASS}
            disabled={currentPage === 1}
            onClick={() => handlePageChange(1)}
            title="First page"
          >
            <ArrowLeftDoubleSVG
              className={cn("size-4", currentPage === 1 && "text-gray-400")}
            />
          </button>

          <button
            type="button"
            className={CONTROL_BUTTON_CLASS}
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
            title="Previous page"
          >
            <ArrowLeftSVG
              className={cn("size-4", currentPage === 1 && "text-gray-400")}
            />
          </button>

          {renderPageNumbers()}

          <button
            type="button"
            className={CONTROL_BUTTON_CLASS}
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
            title="Next page"
          >
            <ArrowRightSVG
              className={cn("size-4", currentPage === totalPages && "text-gray-400")}
            />
          </button>

          <button
            type="button"
            className={CONTROL_BUTTON_CLASS}
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(totalPages)}
            title="Last page"
          >
            <ArrowRightDoubleSVG
              className={cn(
                "size-4",
                currentPage === totalPages && "text-gray-400"
              )}
            />
          </button>
        </div>

        <div className="z-[1] flex shrink-0 items-center gap-[10px]">
          <span className="text-[14px] font-semibold leading-normal text-[#1F1F1F]">
            Page
          </span>
          <div className="relative w-[70px]">
            <select
              className="h-[42px] w-full appearance-none rounded-[8px] border border-[#DDD] bg-white py-[8px] pl-[15px] pr-[32px] text-[14px] font-semibold text-[#1F1F1F] focus:outline-none focus:ring-1 focus:ring-[#5C88C5]"
              value={currentPage}
              onChange={(e) => handlePageChange(Number(e.target.value))}
            >
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (pageNumber) => (
                  <option key={`option-${pageNumber}`} value={pageNumber}>
                    {pageNumber}
                  </option>
                )
              )}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-[15px] flex items-center">
              <svg
                className="size-4 text-[#1F1F1F]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>
          <span className="whitespace-nowrap text-[14px] font-semibold leading-normal text-[#1F1F1F]">
            of {totalPages}
          </span>
        </div>
      </div>
      </div>
    </div>
  );
}
